// External Imports
import { useContext, useEffect, useState } from "react";
import {
  MdCheck,
  MdClose,
  MdDeleteOutline,
} from "react-icons/md";

// Internal Imports
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import { requestAdminAPI } from "../../api/posh-api";
import Modal from "../UI/Modal";
import SelectPair from "../UI/SelectPair";
import { isoDateToString } from "../../util/format-date";
import { TbChevronDown, TbChevronUp } from "react-icons/tb";
import NoRecordsFound from "../UI/NoRecordsFound";
import { UtilityContext } from "../../context/util-context";
import { truncate } from "../../util/format-text";

/** ========================================================================
 * * Coupon Information Page
 * Renders a page, which displays details of a specific coupon.
 * ========================================================================== */

export default function CouponInfo({ open, setOpen, onComplete, couponId }) {
  const { navigate } = useContext(UtilityContext);

  // Initialize States
  const [loading, setLoading] = useState(true);
  const [coupon, setCoupon] = useState({});
  const [usage, setUsage] = useState(false);

  /* ======================= FETCH COUPON ======================= */
  const fetchCouponHandler = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch", {
        table: "coupon",
        id: couponId,
        columns: [
          "customer { firstName lastName }",
          "createdBy { name }",
          "usage: couponUsages_on_coupon { dateCreated payment { id discount description customer { id firstName lastName } } }",
        ],
      });
      setCoupon(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= UPDATE COUPON ======================= */
  const updateHandler = async () => {
    try {
      setLoading(true);
      await requestAdminAPI("edit-coupon", {
        reusable: document.getElementById("reusable").value === "Yes",
        id: coupon.id,
      });
      onComplete && onComplete();
      setOpen(false);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= DELETE COUPON ======================= */
  const deleteHandler = async () => {
    if (coupon.used) return;
    try {
      setLoading(true);
      await requestAdminAPI("edit-coupon", {
        toDelete: true,
        id: coupon.id,
      });
      onComplete && onComplete();
      setOpen(false);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Fetch Coupon on Load
  useEffect(() => {
    if (couponId) fetchCouponHandler();
  }, [couponId]);

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal lg:w-[45vw]">
        <Heading
          title="Coupon Details"
          text="Used coupon cannot be deleted. However, you can disable resuability to prevent further use."
          isBackArrow={false}
        >
          {!coupon.used && (
            <MdDeleteOutline
              className="icon"
              onClick={deleteHandler}
              title="Delete Coupon"
            />
          )}
          <MdCheck
            className="icon"
            onClick={updateHandler}
            title="Edit Coupon"
          />
          <MdClose
            className="icon"
            onClick={() => setOpen(false)}
            title="Close"
          />
        </Heading>

        <div className="info">
          <DetailPair
            label="Date Created"
            value={isoDateToString(coupon.dateCreated)}
            loading={loading}
          />
          <DetailPair label="Coupon Code" value={coupon.id} loading={loading} />
          <DetailPair label="Type" value={coupon.type} loading={loading} />
          <DetailPair
            label="Amount"
            value={
              coupon.type === "fixed"
                ? coupon.amount
                : `${(coupon.amount * 100).toFixed(2)}%`
            }
            loading={loading}
          />
          <DetailPair
            label="Created By"
            value={coupon.createdBy?.name}
            link={`/employees/info?id=${coupon.createdById}`}
            loading={loading}
          />
          <DetailPair
            label="Exclusive"
            value={
              coupon.customerId
                ? `${coupon.customer?.firstName} ${coupon.customer?.lastName}`
                : "No"
            }
            link={
              coupon.customerId
                ? `/customers/info?id=${coupon.customerId}`
                : false
            }
            loading={loading}
          />
          <DetailPair
            label="Used"
            value={coupon.used ? "Yes" : "No"}
            loading={loading}
          />
          <SelectPair
            label="Reusable"
            edit={true}
            loading={loading}
            options={["No", "Yes"]}
            inputOptions={{
              id: "reusable",
              defaultValue: coupon.reusable ? "Yes" : "No",
            }}
          />
        </div>

        <Heading
          title="Usage"
          isBackArrow={false}
          onClick={() => setUsage(!usage)}
        >
          {usage ? (
            <TbChevronDown className="icon" onClick={() => setUsage(false)} />
          ) : (
            <TbChevronUp className="icon" onClick={() => setUsage(true)} />
          )}
        </Heading>
        {usage &&
          (coupon.usage?.length > 0 ? (
            <div className="list">
              {coupon.usage?.map((u, idx) => (
                <div
                  onClick={(e) =>
                    navigate(`/payments/info?id=${u.payment.id}`, e)
                  }
                  key={idx}
                  className="grid pill lg:grid-cols-5"
                >
                  <p className="text-stone-400">
                    {isoDateToString(u.dateCreated)}
                  </p>
                  <p className="font-semibold">
                    {u.payment.customer?.firstName}{" "}
                    {u.payment.customer?.lastName}
                  </p>
                  <p className="lg:col-span-2 text-stone-400">{truncate(u.payment?.description)}</p>
                  <p className="font-semibold">-${u.payment.discount}</p>
                </div>
              ))}
            </div>
          ) : (
            <NoRecordsFound />
          ))}
      </div>
    </Modal>
  );
}
